import React, { createContext, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase_client';
import { useAuth } from './AuthContext';
import { useCompany } from './CompanyContext';
import type { User, Session } from '@supabase/supabase-js';

interface UserProfile {
  id: string;
  display_name?: string;
  locale?: string;
  is_admin: boolean;
  is_superadmin: boolean;
  created_at?: string;
  contact_number?: string;
}

interface OrgPermission {
  orgId: string;
  orgName: string;
  address?: string;
  countryIso?: string;
  roleId: number;
  roleKey: string;
  isActive: boolean;
  permissions: { [key: string]: boolean };
  isPartner: boolean;
  chatwoot_inbox_token: string;
}

interface SessionContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  profileMissing: boolean; // true when authenticated but no user_profiles row exists
  orgs: OrgPermission[];
  activeOrgId: string | null;
  activeOrg: OrgPermission | null;
  isAdmin: boolean; // Global admin (from user_profiles.is_admin)
  isSuperadmin: boolean; // Global superadmin (from user_profiles.is_superadmin)
  isUserAdmin: boolean; // Org admin (role_id 1, 2, or 3) for active org
  permissions: { [key: string]: boolean }; // Permissions for active org
  loading: boolean;
  refresh: () => Promise<void>;
  hasPermission: (perm: string) => boolean;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType>({
  user: null,
  session: null,
  profile: null,
  profileMissing: false,
  orgs: [],
  activeOrgId: null,
  activeOrg: null,
  isAdmin: false,
  isSuperadmin: false,
  isUserAdmin: false,
  permissions: {},
  loading: true,
  refresh: async () => { },
  hasPermission: () => false,
  signOut: async () => { },
});

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session, signOut, loading: authLoading } = useAuth();
  const { selectedCompany, setSelectedCompany } = useCompany();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileMissing, setProfileMissing] = useState(false);
  const [orgs, setOrgs] = useState<OrgPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [readyUserId, setReadyUserId] = useState<string | null>(null);

  const isFetchingRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  // Cleanup on unmount - prevent state updates after unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Get active org from selectedCompany or first org
  const activeOrgId = selectedCompany?.company_id || orgs[0]?.orgId || null;
  const activeOrg = orgs.find(o => o.orgId === activeOrgId) || null;

  // Sync selectedCompany: validate it belongs to current user's orgs
  useEffect(() => {
    if (orgs.length === 0) return;

    if (selectedCompany && !orgs.find(o => o.orgId === selectedCompany.company_id)) {
      const first = orgs[0];
      setSelectedCompany({
        company_id: first.orgId,
        company_name: first.orgName,
        address: first.address || '',
      });
      return;
    }

    if (!selectedCompany) {
      const first = orgs[0];
      setSelectedCompany({
        company_id: first.orgId,
        company_name: first.orgName,
        address: first.address || '',
      });
    }
  }, [orgs, selectedCompany, setSelectedCompany]);

  const fetchUserData = async (userId: string) => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      return;
    }

    // Skip if we already fetched for this user
    if (lastUserIdRef.current === userId && orgs.length > 0 && profile) {
      return;
    }

    try {
      isFetchingRef.current = true;
      if (mountedRef.current) {
        setLoading(true);
      }

      // Fetch user profile
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) {
        if (profileError.code === 'PGRST116') {
          // No profile row — user authenticated but never completed setup
          if (mountedRef.current) {
            setProfile(null);
            setProfileMissing(true);
          }
          // Don't return — still load orgs so ProtectedRoute has full context
        } else {
          console.error('SessionContext: Error fetching profile:', profileError);
          return;
        }
      } else if (profileData && mountedRef.current) {
        setProfile(profileData as UserProfile);
        setProfileMissing(false);
      }

      // Fetch all orgs + roles + permissions in ONE query
      const { data: rbacData, error: rbacError } = await supabase
        .rpc('get_user_org_rbac', { p_user_id: userId });

      if (rbacError) {
        console.error('SessionContext: Error fetching RBAC:', rbacError);
        // Don't sign out on error - let Supabase handle auth state
        if (mountedRef.current) {
          setOrgs([]);
        }
        return;
      }
      // Transform the data
      const mappedOrgs: OrgPermission[] = (rbacData || []).map((row: any) => {
        // Convert permissions array to object
        const permissionsObj: { [key: string]: boolean } = {};
        if (row.permissions && Array.isArray(row.permissions)) {
          row.permissions.forEach((p: any) => {
            if (p && typeof p === 'object') {
              permissionsObj[p.name] = p.allowed || false;
            }
          });
        }

        return {
          orgId: row.org_id,
          orgName: row.org_name,
          address: row.address,
          countryIso: row.country_iso,
          roleId: row.role_id,
          roleKey: row.role_key || '',
          isActive: row.is_active,
          permissions: permissionsObj,
          isPartner: row.is_partner || false,
          chatwoot_inbox_token: row.chatwoot_inbox_token || '',
        };
      });

      if (mountedRef.current) {
        setOrgs(mappedOrgs);
        lastUserIdRef.current = userId;
        setReadyUserId(userId);

        // If no selectedCompany, set the first org
        if (!selectedCompany && mappedOrgs.length > 0) {
          const firstOrg = mappedOrgs[0];
          setSelectedCompany({
            company_id: firstOrg.orgId,
            company_name: firstOrg.orgName,
            address: firstOrg.address || '',
          });
        }
      }
    } catch (error) {
      console.error('SessionContext: Exception fetching user data:', error);
      // Don't sign out on error - let Supabase handle auth state
      if (mountedRef.current) {
        setProfile(null);
        setOrgs([]);
        setReadyUserId(userId);
      }
      lastUserIdRef.current = null;
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setReadyUserId((current) => current ?? userId);
      }
      isFetchingRef.current = false;
    }
  };

  // Fetch data when user changes
  useEffect(() => {
    // Wait for AuthContext to finish loading
    if (authLoading) return;

    if (!user) {
      if (mountedRef.current) {
        setProfile(null);
        setProfileMissing(false);
        setOrgs([]);
        setReadyUserId(null);
        setLoading(false);
      }
      lastUserIdRef.current = null;
      return;
    }

    if (mountedRef.current && readyUserId !== user.id) {
      setLoading(true);
    }
    fetchUserData(user.id);
  }, [user?.id, authLoading]);

  const isAdmin = profile?.is_admin ?? false;
  const isUserAdmin = activeOrg ? [1, 2].includes(activeOrg.roleId) : false;
  const permissions = activeOrg?.permissions || {};
  const isSuperadmin = profile?.is_superadmin ?? false;
  const hasPermission = (perm: string): boolean => {
    if (!activeOrg) return false;
    return activeOrg.permissions[perm] === true;
  };

  const refresh = async () => {
    lastUserIdRef.current = null; // Clear cache
    if (user) {
      await fetchUserData(user.id);
    }
  };

  const value = useMemo(
    () => {
      return {
        user,
        session,
        profile,
        profileMissing,
        orgs,
        activeOrgId,
        activeOrg,
        isAdmin,
        isSuperadmin,
        isUserAdmin,
        permissions,
        loading: authLoading || loading || (!!user && readyUserId !== user.id),
        refresh,
        hasPermission,
        signOut,
      };
    },
    [user, session, profile, profileMissing, orgs, activeOrgId, activeOrg, isAdmin, isSuperadmin, isUserAdmin, permissions, loading, authLoading, readyUserId, signOut]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = () => useContext(SessionContext);